import { useCallback, useEffect, useState } from 'react';
import { COUNTRIES_COLLECTION_NAME } from '../../../../../constants/dealerLocator.constants';
import { fetchDataFromFirebaseDB } from '../../../dealerLocator.utils';

import {
  TAreaPolygonData,
  TCountriesFirebaseCollectionResponse,
  TCountry,
  TUseContryBordersCoordsProps,
} from '../../../types/dealerLocatorLanding.types';
import useFirebase from './useFirebase.effect';

const useContryBordersCoords = ({ model }: TUseContryBordersCoordsProps): Array<TAreaPolygonData> => {
  const [countriesPolygonData, setCountriesPolygonData] = useState<Array<TAreaPolygonData>>([]);
  const [countryDataLoaded, setCountryDataLoaded] = useState<boolean>(false);
  const { app, firebase } = useFirebase();

  const processResponseData = useCallback(
    (data: TCountriesFirebaseCollectionResponse): Array<TAreaPolygonData> => {
      const countryNamesMap = new Map(model.countries?.map((country) => [country.IsoCode, country.Country]));
      const countryPolygons = data?.map((c) => {
        const polygonData: TAreaPolygonData = {
          kml: JSON.parse(c?.kml_4326),
          name: countryNamesMap.get(c?.iso_a2) ?? c?.iso_a2,
          code: c?.iso_a2,
        };

        return polygonData;
      });

      return countryPolygons;
    },
    [model.countries]
  );

  useEffect(() => {
    if (!app || !firebase || !model.hasInteractiveMap || countryDataLoaded) {
      return;
    }

    const countryGroups = model.countries.reduce((acc: [[TCountry]], _item: TCountry, index: number, arr: [TCountry]) => {
      const itemsTakeCount = 10; // Firebase can handle UP to 10 items in value for 'in' query operator
      const takeStartIndex = index * itemsTakeCount;
      const takeEndIndex = takeStartIndex + itemsTakeCount;
      if (takeStartIndex < arr.length) {
        return [...acc, arr.slice(takeStartIndex, takeEndIndex)];
      }
      return acc;
    }, []);

    const contryPromises = countryGroups.map((group) => {
      const queryParams = {
        fieldPath: 'iso_a2',
        opStr: 'in',
        // Firebase can handle UP to 10 items in value for 'in' query operator
        value: group.map((_country) => _country.IsoCode),
      };

      return fetchDataFromFirebaseDB({ app, firebase, collectionName: COUNTRIES_COLLECTION_NAME, queryParams });
    });

    Promise.all(contryPromises)
      .then((data: [TCountriesFirebaseCollectionResponse]) => {
        setCountryDataLoaded(true);
        setCountriesPolygonData(processResponseData(data.flat()));
      })
      .catch((err) => {
        console.error('Error: Unable to fetch Country borders data', err);
        setCountryDataLoaded(true);
        setCountriesPolygonData([]);
      });
  }, [app, firebase, model.hasInteractiveMap, model.countries]);

  return countriesPolygonData;
};

export default useContryBordersCoords;
